import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, CircularProgress } from "@mui/material";
import StyledModal from "../../Modals/StyledModal/StyledModal";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext, ActiveUserContext } from "../../Dashboard/Dashboard";

export default function RejectOfferModal({
  open,
  setOpen,
  offersRejected,
  previousOffers,
  newOffersCount,
  isManager,
  setRefreshPage,
  setRefreshLoads
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [customReason, setCustomReason] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  let [searchParams, setSearchParams] = useSearchParams();

  const rejectReasons = [
    {
      value: "Carrier does not meet service requirements"
    },
    {
      value: "Driver dead head too far for pickup"
    },
    {
      value: "Driver will not make pickup appt time"
    }
  ];

  const handleClose = () => {
    setOpen(false);
    handleReset();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleReset = () => {
    setCustomReason("");
    setSelectedReason("Select one");
  };

  const handleReject = () => {
    setIsLoading(true);
    offersRejected.forEach(offer => {
      let payload = {
        userId: activeUser.userIdMcld,
        isManager: isManager,
        carrierOfferId: offer.id,
        status: "rejected",
        reason: selectedReason || customReason
      };

      CarrierService.UpdateOfferAction(instance, payload).then(() => {
        setIsLoading(false);
        // Fetch all the approved offers
        const previousOffersApproved = previousOffers.filter(({ approvedStatus }) => approvedStatus !== "null" && approvedStatus === "0");
        const isAllOffersRejected = offersRejected.length === newOffersCount;
        const isAllPreviousOffersRejected = offersRejected.length === previousOffersApproved.length;
        if (isAllOffersRejected || isAllPreviousOffersRejected) {
          searchParams.delete("orderId");
          setSearchParams(searchParams);
          setRefreshLoads(true);
        }
        setRefreshPage(true);
        handleClose();
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
    );
  };

  const handleReasonChange = (selected, custom) => {
    if (selected) {
      setSelectedReason(selected);
      setCustomReason("");
    } else {
      setCustomReason(custom);
      setSelectedReason("");
    }
  };

  useEffect(() => {
    if (selectedReason || customReason) {
      setSubmitDisabled(false);
    } else {
      setSubmitDisabled(true);
    }
  }, [selectedReason, customReason]);

  return (
    <StyledModal
      label="Reject Offer"
      title="Reject Offer"
      open={open}
      handleClose={handleClose}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box>
          <Typography variant="body1" color="black_opacity.100">
            Please attach a reason for rejection or input your own.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", paddingTop: "20px" }}>
            <PrimarySelect
              options={rejectReasons.map((o) => (o))}
              onChange={o => handleReasonChange(o, null)}
              value={selectedReason}
              nameKey="value"
              valueKey="value"
              width="300px"
              defaultOption="Select one"
              disabled={isLoading || customReason != ""}
            />
            <Typography variant="body1" sx={{ color: "black_opacity.100", marginX: "20px" }}>or</Typography>
            <PrimaryTextField
              id="reject-offer-modal-custom-response"
              onChange={o => handleReasonChange(null, o)}
              value={customReason}
              placeholder="Type a custom response here"
              width="300px"
              disabled={isLoading || selectedReason != ""}
            />
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", marginTop: "20px" }}>
        <Box sx={{ marginLeft: "auto", padding: "5px 10px 5px 5px" }}>
          <SecondaryButton
            label="Cancel"
            onClick={handleCancel}
            disabled={isLoading}
          />
        </Box>
        <Box sx={{ padding: "5px 0 5px 5px" }}>
          <PrimaryButton
            label="Reject Offer"
            onClick={handleReject}
            isLoading={isLoading}
            disabled={submitDisabled}
          />
        </Box>
      </Box>
    </StyledModal>
  );
}

RejectOfferModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  offersRejected: PropTypes.array.isRequired,
  previousOffers: PropTypes.array,
  newOffersCount: PropTypes.number,
  isManager: PropTypes.bool,
  setRefreshPage: PropTypes.func,
  setRefreshLoads: PropTypes.func
};